import { Trans } from '@lingui/react/macro';
import type { ReactNode } from 'react';
import type { BusinessDataConnection } from '../../services/businessDataConnectionService';
import type { BusinessDataOperation } from '../../services/businessDataService';
import { ConnectionStatusBadge } from './ConnectionStatusBadge';
import { ConnectionHealthPanel } from './ConnectionHealthPanel';
import { ConnectionAuthError } from './ConnectionAuthError';
import { ConnectionActions } from './ConnectionActions';
import { PlusIcon } from './icons';

interface Props {
  name: string;
  logo: ReactNode;
  conn?: BusinessDataConnection;
  scanJob?: BusinessDataOperation;
  isScanning: boolean;
  isLegacy?: boolean;
  onConnect: () => void;
  onScan: (conn: BusinessDataConnection) => void;
  onReconnect: (conn: BusinessDataConnection) => void;
  onUpgrade?: () => void;
  onDisconnect: (conn: BusinessDataConnection) => void;
}

const SHELL = 'group relative text-left rounded-xl border p-4 transition-all';
const LOGO_WRAP = 'w-9 h-9 rounded-lg flex items-center justify-center border shadow-sm';
const SURFACE = { backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' };

// Border + background treatment for a *connected* card, by state.
function connectedTone(conn: BusinessDataConnection, scanJob: BusinessDataOperation | undefined, isScanning: boolean): string {
  if (conn.status === 'error' || scanJob?.status === 'error') return 'border-red-300 dark:border-red-800/60 bg-red-50/40 dark:bg-red-500/5';
  if (isScanning) return 'border-blue-300 dark:border-blue-800/60 bg-blue-50/40 dark:bg-blue-500/5';
  return 'border-green-300 dark:border-green-800/60 bg-green-50/50 dark:bg-green-500/5';
}

// One service card. Disconnected and connected are two distinct shapes, split by
// an early return so neither path carries the other's conditionals.
export function ConnectionCard({ name, logo, conn, scanJob, isScanning, isLegacy, onConnect, onScan, onReconnect, onUpgrade, onDisconnect }: Props) {
  if (!conn) {
    return (
      <div className={`${SHELL} hover:border-blue-300 dark:hover:border-blue-700`} style={SURFACE}>
        <div className="flex items-start justify-between mb-3">
          <div className={LOGO_WRAP} style={SURFACE}>{logo}</div>
          <PlusIcon className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity text-blue-500" />
        </div>
        <h3 className="text-sm font-semibold mb-2.5" style={{ color: 'var(--color-text)' }}>{name}</h3>
        <p className="text-xs leading-relaxed line-clamp-2 mb-3" style={{ color: 'var(--color-text-muted)' }}><Trans>Click to connect</Trans></p>
        <button
          onClick={onConnect}
          className="w-full py-1.5 rounded-lg text-xs font-medium transition-colors text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20"
        >
          <Trans>Connect</Trans>
        </button>
      </div>
    );
  }

  const authFailed = conn.status === 'error';
  return (
    <div className={`${SHELL} ${connectedTone(conn, scanJob, isScanning)}`}>
      <div className="flex items-start justify-between mb-3">
        <div className={LOGO_WRAP} style={SURFACE}>{logo}</div>
        <ConnectionStatusBadge conn={conn} scanJob={scanJob} isScanning={isScanning} />
      </div>
      <h3 className="text-sm font-semibold mb-2.5" style={{ color: 'var(--color-text)' }}>{name}</h3>

      {isLegacy && !authFailed && (
        <p className="text-[11px] font-medium mb-2 text-amber-600 dark:text-amber-400"><Trans>Legacy credentials — OAuth recommended</Trans></p>
      )}

      {authFailed
        ? <ConnectionAuthError conn={conn} scanJob={scanJob} />
        : <ConnectionHealthPanel scanJob={scanJob} isScanning={isScanning} />}

      <ConnectionActions
        conn={conn}
        scanJob={scanJob}
        isScanning={isScanning}
        onScan={onScan}
        onReconnect={onReconnect}
        onUpgrade={isLegacy ? onUpgrade : undefined}
        onDisconnect={onDisconnect}
      />
    </div>
  );
}
